import { Suspense, useEffect, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { App as AntdApp, Spin } from 'antd';
import { Route, Routes } from 'react-router-dom';

import { MAINTENANCE_CLIENT } from './API_Utils';
import AuthError from './Components/Common/AuthError';
import LegalNoticeModal from './Components/Common/LegalNoticeModal';
import NoRolesError from './Components/Common/NoRolesError';
import { USER_ROLE_KEY } from './constants';
import { setMessage } from './MessageHook';
import GET_BULK_UPLOADER_PATH from './Pages/BulkUploaders/BulkUploaderPaths';
import { Error403, Maintenance } from './Pages/ErrorPages';
import GET_USER_MANAGEMENT_PATHS from './Pages/UserMangement/userManagement';
import './App.css';
import routeMap from './Routes';
import { USER_ACCESS_MODES, createUserAccessProfile } from './Utils/userAccessPolicy';

// const config = {
//   domain: process.env.REACT_APP_AUTH0_DOMAIN,
//   audience: process.env.REACT_APP_AUDIENCE,
//   clientId: process.env.REACT_APP_CLIENTID
// };

// Create a separate component for routes that need authentication
const AuthenticatedRoutes = () => {
  const { user, isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently, error } = useAuth0();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      loginWithRedirect();
    }
    if (user) {
      getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUDIENCE
        }
      })
        .then((t) => localStorage.setItem('token', t))
        .catch(() => {});
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, error, user, getAccessTokenSilently]);

  if (error) {
    return <AuthError error={error.error || 'unknown_error'} errorDescription={error.message} />;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // At this point, if not authenticated, loginWithRedirect is in progress
  if (!isAuthenticated) {
    // Just show nothing or a spinner since redirect will happen
    return <Spin />;
  }

  const groups = user?.[USER_ROLE_KEY];
  const accessProfile = createUserAccessProfile(groups);
  const normalizedGroups = Array.isArray(groups) ? groups : [];

  if (accessProfile.mode === USER_ACCESS_MODES.NONE) {
    return <NoRolesError email={user?.email} />;
  }

  return (
    <>
      <LegalNoticeModal />
      <Routes>
        <Route element={<Error403 />} path="403" />
        <Route path="/">
          {routeMap(accessProfile, normalizedGroups).map((element) => (
            <Route
              key={element?.key}
              element={<Suspense fallback={<div>Loading...</div>}>{element.value}</Suspense>}
              path={element.path}
            />
          ))}
          {accessProfile.canAccessAuxiliaryRoutes ? (
            <>
              <Route path="/bulkuploader/">
                {GET_BULK_UPLOADER_PATH(normalizedGroups).map((item) => (
                  <Route key={item.key} element={item.element} path={item.path} />
                ))}
              </Route>
              <Route path="/users/">
                {GET_USER_MANAGEMENT_PATHS(normalizedGroups)?.map((item) => (
                  <Route key={item.key} element={item.element} path={item.path} />
                ))}
              </Route>
            </>
          ) : null}
        </Route>
        <Route element={<Error403 />} path="/*" />
      </Routes>
    </>
  );
};

const App = () => {
  const { message } = AntdApp.useApp();
  const [maintenanceConfig, setMaintenanceConfig] = useState(null);
  const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(true);

  useEffect(() => {
    setMessage(message);
  }, [message]);

  useEffect(() => {
    MAINTENANCE_CLIENT.getMaintenanceConfig(
      setIsLoadingMaintenance,
      (data) => {
        setMaintenanceConfig(data);
      },
      () => {
        setMaintenanceConfig({ isMaintenanceMode: false });
      }
    );
  }, []);

  const isMaintenanceMode = maintenanceConfig?.isMaintenanceMode || false;

  // const navigate = useNavigate();

  // const onRedirectCallback = (appState) => {
  //   navigate(appState && appState.returnTo ? appState.returnTo : window.location.pathname);
  // };

  // const providerConfig = {
  //   domain: config.domain,
  //   clientId: config.clientId,
  //   onRedirectCallback,
  //   authorizationParams: {
  //     redirect_uri: window.location.origin,
  //     ...(config.audience ? { audience: config.audience } : null)
  //   }
  // };

  if (isLoadingMaintenance) {
    return (
      <AntdApp>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Spin size="large" />
        </div>
      </AntdApp>
    );
  }

  if (isMaintenanceMode) {
    return (
      <AntdApp>
        <Maintenance config={maintenanceConfig} />
      </AntdApp>
    );
  }

  return (
    // <Auth0Provider {...providerConfig}>
    <AntdApp>
      <div className="App">
        <AuthenticatedRoutes />
      </div>
    </AntdApp>
    // </Auth0Provider>
  );
};

export default App;
